// components/HeroAttractionSection.client.tsx
"use client";

import * as React from "react";
import clsx from "clsx";
import Image from "next/image";
import CtaButton from "@/components/ui/CtaButton";
import Breadcrumbs, { type BreadcrumbItem } from "./Breadcrumbs";
import { LazyMotion, domAnimation, m, type MotionProps } from "framer-motion";

export type HeroAttractionImage = {
    src: string;
    alt?: string;
};

type Props = {
    breadcrumbs?: BreadcrumbItem[];
    heading: string;
    description: string;
    images: HeroAttractionImage[];
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof Props | keyof MotionProps | ConflictingHtmlEvents
>;

export type HeroAttractionSectionClientProps = Props & SafeSectionHtmlProps & MotionProps;

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

const revealSide = {
    hidden: { clipPath: "inset(0% 0% 0% 100%)", opacity: 0 },
    visible: {
        clipPath: "inset(0% 0% 0% 0%)",
        opacity: 1,
        transition: { duration: 1.4, ease: [0.45, 0, 0.55, 1], delay: 0.3 },
    },
} as const;

function MarqueeRow({
                        images,
                        marqueeSizes,
                        offsetClassName,
                        rowId,
                    }: {
    images: HeroAttractionImage[];
    marqueeSizes: string;
    offsetClassName?: string;
    rowId: "top" | "bottom";
}) {
    const loops = React.useMemo(() => Array.from({ length: 2 }), []);

    return (
        <div
            className={clsx(
                offsetClassName,
                "grid w-full animate-marquee-horizontally auto-cols-fr grid-cols-2 gap-4 self-center"
            )}
        >
            {loops.map((_, loopIndex) => (
                <div key={`${rowId}-${loopIndex}`} className="grid w-full grid-flow-col gap-4">
                    {images.map((image, imageIndex) => {
                        const isHeroImage = rowId === "top" && loopIndex === 0 && imageIndex === 0;

                        return (
                            <div
                                key={`${rowId}-${loopIndex}-${imageIndex}`}
                                className="relative w-[60vw] pt-[75%] sm:w-[18rem] md:w-[26rem]"
                            >
                                <Image
                                    src={image.src}
                                    alt={image.alt || ""}
                                    fill
                                    sizes={marqueeSizes}
                                    draggable={false}
                                    className="absolute inset-0 size-full object-cover rounded-3xl"
                                    priority={isHeroImage}
                                    fetchPriority={isHeroImage ? "high" : "low"}
                                    loading={isHeroImage ? "eager" : "lazy"}
                                    decoding="async"
                                />
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

export default function HeroAttractionSectionClient(props: HeroAttractionSectionClientProps) {
    const { breadcrumbs, heading, description, images, className, ...rest } = props;

    const marqueeSizes = React.useMemo(
        () => "(max-width: 767px) 60vw, (max-width: 1023px) 288px, 416px",
        []
    );

    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="relume"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className={clsx(
                    "grid grid-cols-1 items-center gap-y-6 pt-36 md:pt-54 lg:grid-cols-2 lg:pt-0 pb-8 md:pb-28",
                    className
                )}
                {...rest}
            >
                {/* LEWA KOLUMNA – TEKST */}
                <div className="mx-[5%] sm:max-w-md md:justify-self-start lg:ml-[5vw] lg:mr-20 lg:justify-self-end">
                    {breadcrumbs && breadcrumbs.length > 0 ? (
                        <m.div variants={slideUp} custom={0} className="mb-4 md:mb-6 will-change-transform">
                            <Breadcrumbs items={breadcrumbs} />
                        </m.div>
                    ) : (
                        <m.p
                            variants={slideUp}
                            custom={0}
                            className="uppercase tracking-[0.10em] text-sm mb-2 md:mb-4 will-change-transform"
                        >
                            Atrakcja
                        </m.p>
                    )}

                    <m.h1
                        variants={slideUp}
                        custom={1}
                        className="mb-5 text-5xl font-medium md:mb-6 md:text-7xl will-change-transform"
                    >
                        {heading}
                    </m.h1>

                    <m.p variants={slideUp} custom={2} className="md:text-md will-change-transform">
                        {description}
                    </m.p>

                    <m.div variants={slideUp} custom={3} className="mt-6 flex flex-wrap gap-4 md:mt-8 will-change-transform">
                        <CtaButton />
                    </m.div>
                </div>

                {/* PRAWA KOLUMNA – MARQUEE */}
                <m.div
                    variants={revealSide}
                    className="flex items-center gap-4 overflow-hidden mt-6 md:mt-24 bg-secondary md:rounded-bl-[32px] md:rounded-tl-[32px] py-3 md:py-6"
                >
                    <div className="grid shrink-0 grid-cols-1 gap-y-4">
                        <MarqueeRow images={images} marqueeSizes={marqueeSizes} offsetClassName="ml-[-22%]" rowId="top" />
                        <MarqueeRow images={images} marqueeSizes={marqueeSizes} rowId="bottom" />
                    </div>
                </m.div>
            </m.section>
        </LazyMotion>
    );
}